import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CaretRight, Check, CircleNotch, Copy, Download, Kanban, Pause, Play, PencilSimple, Sparkle, SpeakerHigh, Trash, VideoCamera, Target, X } from "@phosphor-icons/react";
import { AIOutput, ChipSelector, CopyButton } from "@/components/shared";
import { API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOAL_OPTIONS = [
  { value: "signups", label: "Drive signups to my platform / tool" },
  { value: "sales", label: "Sell a product / book / service" },
  { value: "followers", label: "Grow followers / audience" },
  { value: "authority", label: "Educate / build authority" },
  { value: "nurture", label: "Nurture existing audience" },
  { value: "waitlist", label: "Join waitlist / early access" },
  { value: "custom", label: "Custom (write your own below)" },
];

// Fallback topics used ONLY when /topics/personalized is unreachable. Every
// live request pulls topics generated from the signed-in user's Brand Knowledge
// + industry + North Star so each account sees its own suggestions.
const FALLBACK_TOPICS = [
  "The biggest lesson I learned this year",
  "What nobody tells you about building a business",
  "The mindset shift that changed everything",
  "Why consistency beats perfection",
  "The tool I wish I had from day one",
];

const ScriptStudio = () => {
  const { user } = useAuth();
  const northStar = user?.north_star || {};
  const defaultCta = northStar.primary_cta || "";
  const defaultGoal = northStar.conversion_metric || "";
  const ctaOptions = [
    defaultCta,
    ...(northStar.alt_ctas || []),
  ].filter(Boolean);

  const [angles, setAngles] = useState([]);
  const [form, setForm] = useState({
    platform: "tiktok",
    length: "45-60sec",
    tone: "gossip-wisdom",
    angle: "",
    topic: "",
    goal: defaultGoal,
    takeaway: "",
    cta: defaultCta,
  });
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFilename, setAudioFilename] = useState(null);
  const [generatingVoice, setGeneratingVoice] = useState(false);
  // Voice provider state — "openai" = free stock voices, "elevenlabs" = BYOK cloned.
  const [voiceProvider, setVoiceProvider] = useState("openai");
  const [openaiVoice, setOpenaiVoice] = useState("nova");
  const [openaiVoices, setOpenaiVoices] = useState([]);
  const [elevenlabsVoice, setElevenlabsVoice] = useState("");
  const [elevenlabsVoices, setElevenlabsVoices] = useState([]);
  const [elevenlabsAvailable, setElevenlabsAvailable] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [voiceFavorites, setVoiceFavorites] = useState([]); // ["openai:nova", "elevenlabs:xxx"]
  const [isPlaying, setIsPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Personalized topic list (generated per-user from brand knowledge +
  // North Star). Each entry is {topic, used, source}; we only render .topic.
  const [personalizedTopics, setPersonalizedTopics] = useState([]);
  const [refreshingTopics, setRefreshingTopics] = useState(false);
  // Talking Photo (Auto Create) state — uses BYOK Magic Hour in Realistic mode.
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [videoHistory, setVideoHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Provider preference — same picker as AutoCreate. "auto" = Fabric → Magic Hour fallback.
  const [talkingPhotoProvider, setTalkingPhotoProvider] = useState("auto");
  const [talkingPhotoResolution, setTalkingPhotoResolution] = useState("480p");
  const audioRef = React.useRef(null);

  const platforms = ["tiktok", "instagram", "linkedin", "live"];
  const lengths = ["30sec", "45-60sec", "1-2min", "2-3min", "15min-live", "30min-webinar"];
  const LONG_FORM = new Set(["15min-live", "30min-webinar"]);
  const tones = [
    { value: "gossip-wisdom", label: "Gossip meets Wisdom" },
    { value: "reflective", label: "Reflective" },
    { value: "hard-truth", label: "Hard Truth" },
    { value: "storytelling", label: "Storytelling" },
    { value: "teaching", label: "Teaching Mode" },
  ];

  useEffect(() => {
    const fetchAngles = async () => {
      try {
        const res = await axios.get(`${API}/script/angles`);
        setAngles(res.data.angles);
      } catch (e) {
        console.error("Error fetching angles:", e);
      }
    };
    const fetchTopics = async () => {
      try {
        const res = await axios.get(`${API}/topics/personalized`);
        // API returns [{topic, used, source}, ...] — extract strings only.
        const items = res.data?.topics || [];
        const strs = items.map((t) => (typeof t === "string" ? t : t?.topic)).filter(Boolean);
        if (strs.length) setPersonalizedTopics(strs);
      } catch (e) {
        // If the request fails we keep whatever's in state (probably empty,
        // which triggers the FALLBACK_TOPICS branch in the select below).
        console.warn("Couldn't load personalized topics:", e);
      }
    };
    fetchAngles();
    fetchTopics();
  }, []);

  const refreshTopics = async () => {
    setRefreshingTopics(true);
    try {
      const res = await axios.get(`${API}/topics/personalized`, { params: { refresh: true } });
      const items = res.data?.topics || [];
      const strs = items.map((t) => (typeof t === "string" ? t : t?.topic)).filter(Boolean);
      if (strs.length) setPersonalizedTopics(strs);
    } catch (e) {
      console.warn("Refresh topics failed:", e);
    } finally {
      setRefreshingTopics(false);
    }
  };

  const generateScript = async () => {
    if (!form.topic) return;
    setLoading(true);
    setOutput("");
    setAudioUrl(null);
    setSaved(false);
    const isLong = LONG_FORM.has(form.length);
    try {
      const res = await axios.post(`${API}/script/generate`, form);

      // Long-form (15-min live, 30-min webinar) returns a task_id and we poll
      // until it's done, because the K8s ingress kills HTTP requests > 60s.
      if (res.data?.task_id) {
        setOutput(
          form.length === "30min-webinar"
            ? "Writing your 30-minute webinar script... this typically takes 2-4 minutes. Feel free to keep this tab open — we'll drop the full script in as soon as it's ready."
            : "Writing your 15-minute live script... this typically takes 1-3 minutes. You can leave this tab open."
        );
        const taskId = res.data.task_id;
        const startedAt = Date.now();
        const MAX_MS = 8 * 60 * 1000; // 8 minutes safety ceiling
        while (Date.now() - startedAt < MAX_MS) {
          await new Promise((r) => setTimeout(r, 4000));
          const poll = await axios.get(`${API}/script/task/${taskId}`);
          const data = poll.data || {};
          if (data.status === "completed") {
            setOutput(data.result?.script || "");
            return;
          }
          if (data.status === "failed") {
            throw new Error(data.error || "Long-form generation failed");
          }
        }
        throw new Error("Long-form generation timed out after 8 minutes. Try again — the LLM may have been slow.");
      }

      setOutput(res.data.script);
    } catch (e) {
      console.error("Error generating script:", e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Unknown error";
      let msg;
      if (status === 401) {
        msg = "Your session expired. Redirecting to login...";
        setTimeout(() => { window.location.assign("/login"); }, 1500);
      } else if (status === 402 || /credit/i.test(String(detail))) {
        msg = `Out of credits — top up from Credits & Keys to keep generating. (${detail})`;
      } else if (status === 429) {
        msg = `Rate limited — slow down for a few seconds, then try again. (${detail})`;
      } else if (status >= 500) {
        msg = `Our AI service had a hiccup (HTTP ${status}). ${isLong ? "Long-form scripts can be retried — they auto-recover." : "Try again in 30 seconds."} If it keeps failing, message support with this code: ${detail}`;
      } else if (!status) {
        msg = `Network error — can't reach the server. Check your connection and retry. (${detail})`;
      } else {
        msg = `Error generating script (HTTP ${status}): ${detail}`;
      }
      setOutput(msg);
    } finally {
      setLoading(false);
    }
  };

  const saveToPipeline = async () => {
    if (!output || !form.topic) return;
    setSaving(true);
    try {
      // Extract hook from output if present
      const hookMatch = output.match(/HOOK:\s*(.+?)(?=\n|SCRIPT:)/s);
      const hook = hookMatch ? hookMatch[1].trim() : "";
      
      await axios.post(`${API}/pipeline`, {
        title: form.topic,
        platform: form.platform,
        stage: "scripted",
        script: output,
        hook: hook,
        source: "generated"
      });
      setSaved(true);
    } catch (e) {
      console.error("Error saving to pipeline:", e);
    } finally {
      setSaving(false);
    }
  };

  const generateVoice = async () => {
    if (!output) return;
    setGeneratingVoice(true);
    setAudioUrl(null);
    try {
      // Clean the script text - remove emotion tags for cleaner audio
      const cleanText = output.replace(/\([^)]+\)/g, '').replace(/HOOK:|SCRIPT:/gi, '').trim();
      const payload = {
        text: cleanText,
        provider: voiceProvider,
        voice_id: voiceProvider === "openai" ? openaiVoice : elevenlabsVoice,
      };
      if (voiceProvider === "elevenlabs" && !elevenlabsVoice) {
        throw new Error("Pick a voice from your ElevenLabs library first.");
      }
      const res = await axios.post(`${API}/voice/generate`, payload);

      // Long scripts go async via task_id. Poll until done.
      if (res.data?.task_id) {
        const taskId = res.data.task_id;
        const startedAt = Date.now();
        const MAX_MS = 10 * 60 * 1000;
        while (Date.now() - startedAt < MAX_MS) {
          await new Promise((r) => setTimeout(r, 5000));
          const poll = await axios.get(`${API}/voice/task/${taskId}`);
          const data = poll.data || {};
          if (data.status === "completed") {
            setAudioUrl(`${BACKEND_URL}${data.result.audio_url}`);
            setAudioFilename(data.result.filename);
            return;
          }
          if (data.status === "failed") {
            throw new Error(data.error || "Voice generation failed");
          }
        }
        throw new Error("Voice generation timed out after 10 minutes. Try a shorter script.");
      }

      setAudioUrl(`${BACKEND_URL}${res.data.audio_url}`);
      setAudioFilename(res.data.filename);
    } catch (e) {
      console.error("Error generating voice:", e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Unknown error";
      let msg;
      if (status === 400 && /api key|voice_id/i.test(String(detail))) {
        msg = `${detail}\n\nGo to Account Settings → API Keys to configure your ElevenLabs voice.`;
      } else if (status === 401) {
        msg = "Your session expired. Please log in again.";
      } else if (status === 503) {
        // Third-party provider issue (out of quota etc).
        msg = `${detail}`;
      } else if (status === 504 || /timeout/i.test(String(detail))) {
        msg = `Voice generation timed out. Try a shorter script — or split a long webinar into 5-10 min segments and generate each separately.`;
      } else if (!status) {
        msg = `Network error — can't reach the server. Check your connection and retry. (${detail})`;
      } else {
        msg = `Error generating voice (HTTP ${status || "?"}): ${detail}`;
      }
      alert(msg);
    } finally {
      setGeneratingVoice(false);
    }
  };

  // Load voice providers + voice list + user's saved default + favorites on mount.
  const loadVoiceData = useCallback(async () => {
    try {
      const [voices, providers, pref] = await Promise.all([
        axios.get(`${API}/voice/openai/voices`),
        axios.get(`${API}/voice/providers`),
        axios.get(`${API}/voice/preference`).catch(() => ({ data: {} })),
      ]);
      setOpenaiVoices(voices.data?.voices || []);
      const ell = (providers.data?.providers || []).find((p) => p.id === "elevenlabs");
      setElevenlabsAvailable(Boolean(ell?.available));
      if (ell?.available) {
        const elVoices = await axios.get(`${API}/voice/elevenlabs/voices`);
        setElevenlabsVoices(elVoices.data?.voices || []);
      }
      // Hydrate user's saved default voice + favorites
      if (pref.data?.provider) setVoiceProvider(pref.data.provider);
      if (pref.data?.voice_id) {
        if (pref.data.provider === "elevenlabs") setElevenlabsVoice(pref.data.voice_id);
        else setOpenaiVoice(pref.data.voice_id);
      }
      setVoiceFavorites(pref.data?.favorites || []);
    } catch (e) {
      console.warn("Voice provider load failed:", e);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { loadVoiceData(); }, 0);
    return () => clearTimeout(t);
  }, [loadVoiceData]);

  // Persist the user's voice pick as their global default — used by Auto
  // Create / Bulk Create / Series Studio video generation too.
  const persistVoicePref = useCallback(async (provider, voiceId) => {
    if (!voiceId) return;
    try {
      await axios.post(`${API}/voice/preference`, { provider, voice_id: voiceId });
    } catch (e) {
      console.warn("Could not save voice preference:", e);
    }
  }, []);

  const toggleVoiceFavorite = async (provider, voiceId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const key = `${provider}:${voiceId}`;
    const isFav = voiceFavorites.includes(key);
    const next = isFav ? voiceFavorites.filter((k) => k !== key) : [...voiceFavorites, key];
    setVoiceFavorites(next); // optimistic
    try {
      await axios.post(`${API}/voice/favorites`, { provider, voice_id: voiceId, favorite: !isFav });
    } catch (err) {
      setVoiceFavorites(voiceFavorites); // rollback
      console.error("Favorite toggle failed:", err);
    }
  };

  const previewVoice = async (voiceId) => {
    if (previewingVoice === voiceId) return;
    setPreviewingVoice(voiceId);
    try {
      const resp = await axios.post(
        `${API}/voice/openai/preview`,
        { voice_id: voiceId },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      a.onended = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
      a.play();
    } catch (e) {
      console.error("Preview failed:", e);
      setPreviewingVoice(null);
      alert("Preview failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // HeyGen look selection
  const [looks, setLooks] = useState([]);
  const [selectedLook, setSelectedLook] = useState(null);
  const [loadingLooks, setLoadingLooks] = useState(false);

  // AI Twin gallery — every signed-in user's images. We show ALL of them
  // (no pagination) in a scrollable grid so the user can pick any twin.
  const fetchLooks = useCallback(async () => {
    setLoadingLooks(true);
    try {
      const res = await axios.get(`${API}/ai-twin/images`);
      const images = res.data.images || [];
      // Map AI Twin shape → look shape so the existing render logic keeps
      // working. `image_url` is what the picker `<img>` reads.
      const mapped = images.map((img) => ({
        id: img.id,
        name: img.original_name || "AI Twin",
        image_url: `${BACKEND_URL}/api/ai-twin/images/${img.filename}`,
      }));
      setLooks(mapped);
    } catch (e) {
      console.error("Error fetching AI Twins:", e);
    } finally {
      setLoadingLooks(false);
    }
  }, []);

  // Auto-pick the first AI Twin on initial load when none is selected yet.
  // Kept in a separate effect from fetchLooks so the loader function's
  // identity doesn't depend on `selectedLook` (which would cause re-fetches).
  useEffect(() => {
    const t = setTimeout(() => {
      if (looks.length > 0 && !selectedLook) {
        setSelectedLook(looks[0]);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [looks, selectedLook]);

  const fetchVideoHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/video-history`);
      setVideoHistory(res.data.videos || []);
    } catch (e) {
      console.error("Error fetching video history:", e);
    }
  }, []);

  // Standard fetch-on-mount pattern. The lint rule flags any setState
  // reachable from a useEffect — including via downstream fetchers — but
  // this is the canonical React idiom for loading initial data.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchLooks();
      fetchVideoHistory();
    }, 0);
    return () => clearTimeout(t);
  }, [fetchLooks, fetchVideoHistory]);

  const generateHeyGenVideo = async () => {
    if (!output || !selectedLook) {
      if (!selectedLook) alert("Pick an AI Twin first.");
      return;
    }
    setGeneratingVideo(true);
    setVideoUrl(null);
    setVideoError(null);
    setVideoStatus("starting");
    try {
      // Talking Photo Realistic mode via the Auto Create pipeline — uses the
      // user's own BYOK Magic Hour key (not the legacy platform HeyGen
      // account, which routinely runs out of credits).
      const res = await axios.post(`${API}/auto-create`, {
        topic: form.topic || "Untitled Script",
        platform: form.platform || "tiktok",
        length: form.length || "45-60sec",
        tone: form.tone || "gossip-wisdom",
        angle: form.angle || "",
        image_id: selectedLook.id,
        custom_script: output,
        include_captions: false,
        include_broll: false,
        talking_photo_mode: "realistic",
        talking_photo_provider: talkingPhotoProvider,
        talking_photo_resolution: talkingPhotoResolution,
      });
      const jid = res.data.job_id;
      setVideoJobId(jid);
      setVideoStatus("processing");
      pollVideoStatus(jid);
    } catch (e) {
      console.error("Error generating video:", e);
      setVideoStatus("error");
      setGeneratingVideo(false);
      const detail = e.response?.data?.detail || "Error generating video. Please try again.";
      alert(typeof detail === "string" ? detail : JSON.stringify(detail));
    }
  };

  const pollVideoStatus = (jid) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/auto-create/jobs/${jid}`);
        const st = res.data.status;
        setVideoStatus(st);
        if (st === "completed") {
          setVideoUrl(res.data.output_url);
          setGeneratingVideo(false);
          fetchVideoHistory();
          clearInterval(interval);
        } else if (st === "failed" || st === "error") {
          const errData = res.data.error;
          const errMsg = typeof errData === "object" ? (errData?.detail || errData?.message || "Unknown error") : (errData || "Video generation failed");
          setVideoError(errMsg);
          setGeneratingVideo(false);
          fetchVideoHistory();
          clearInterval(interval);
        }
      } catch (e) {
        console.error("Error polling status:", e);
      }
    }, 5000);
  };

  const deleteVideoFromHistory = async (historyId) => {
    try {
      await axios.delete(`${API}/video-history/${historyId}`);
      setVideoHistory((prev) => prev.filter((v) => v.id !== historyId));
    } catch (e) {
      console.error("Error deleting video:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Script Studio</h2>
        <p className="text-sm text-zinc-500 mt-1">Generate talking-head video scripts in your voice</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="card p-4 sm:p-6 space-y-6">
          <ChipSelector
            label="Platform"
            options={platforms}
            selected={form.platform}
            onChange={(v) => setForm({ ...form, platform: v })}
          />

          <ChipSelector
            label="Length"
            options={lengths}
            selected={form.length}
            onChange={(v) => setForm({ ...form, length: v })}
          />

          <ChipSelector
            label="Tone"
            options={tones}
            selected={form.tone}
            onChange={(v) => setForm({ ...form, tone: v })}
          />

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Angle</label>
            <select
              value={form.angle}
              onChange={(e) => setForm({ ...form, angle: e.target.value })}
              className="input-field"
              data-testid="angle-select"
            >
              <option value="">Select an angle or write your own...</option>
              {angles.map((angle) => (
                <option key={angle} value={angle}>
                  {angle}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Topic</label>
              <button
                type="button"
                onClick={refreshTopics}
                disabled={refreshingTopics}
                className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="refresh-topics-btn"
              >
                {refreshingTopics ? "Thinking…" : "↻ Refresh ideas"}
              </button>
            </div>
            {(() => {
              const topicList = personalizedTopics.length > 0 ? personalizedTopics : FALLBACK_TOPICS;
              return (
                <>
                  <select
                    value={topicList.includes(form.topic) ? form.topic : ""}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="input-field mb-2"
                    data-testid="topic-select"
                  >
                    <option value="">
                      {personalizedTopics.length > 0
                        ? "Choose a topic tailored to your brand, or write your own below…"
                        : "Choose a topic or write your own below…"}
                    </option>
                    {topicList.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                  {personalizedTopics.length === 0 && (
                    <p className="text-[11px] text-zinc-400 -mt-1 mb-2">
                      Add a Brand Knowledge doc to get topics tailored to your niche.
                    </p>
                  )}
                </>
              );
            })()}
            <textarea
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="textarea-field h-24"
              placeholder="Or type your own topic here..."
              data-testid="topic-input"
            />
          </div>

          {/* Conversion Framework — Goal, Takeaway, CTA */}
          <div className="card p-4 space-y-3 border-2 border-[#E91E8C]/25 bg-gradient-to-br from-[#E91E8C]/5 to-transparent" data-testid="conversion-framework">
            <div className="flex items-start gap-2">
              <Target size={18} weight="duotone" className="text-[#E91E8C] mt-0.5" />
              <div>
                <p className="text-sm font-bold text-zinc-900">Conversion Framework</p>
                <p className="text-[11px] text-zinc-500 leading-tight">
                  Pin the goal, the ONE takeaway, and the exact CTA. Every script will be anchored toward this.
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Goal of this video</label>
              <select
                value={GOAL_OPTIONS.find((g) => g.label === form.goal)?.value || (form.goal ? "custom" : "")}
                onChange={(e) => {
                  const opt = GOAL_OPTIONS.find((g) => g.value === e.target.value);
                  if (!opt) return;
                  if (opt.value === "custom") {
                    setForm({ ...form, goal: "" });
                  } else {
                    setForm({ ...form, goal: opt.label });
                  }
                }}
                className="input-field text-sm"
                data-testid="script-goal-select"
              >
                <option value="">Pick a goal or write your own...</option>
                {GOAL_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                placeholder="Or type your own goal (e.g., 'Get 50 free trial signups this week')"
                data-testid="script-goal-input"
                className="input-field text-sm mt-2"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">
                Key takeaway <span className="text-zinc-300 font-normal normal-case">(the ONE thing viewers should remember)</span>
              </label>
              <textarea
                value={form.takeaway}
                onChange={(e) => setForm({ ...form, takeaway: e.target.value })}
                placeholder="e.g., You don't need a production team or budget to build a viral content engine — you just need the right AI stack."
                rows={2}
                data-testid="script-takeaway-input"
                className="input-field text-sm resize-none"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">
                Call to action <span className="text-zinc-300 font-normal normal-case">(used verbatim at the end of the script)</span>
              </label>
              {ctaOptions.length > 0 && (
                <select
                  value={ctaOptions.includes(form.cta) ? form.cta : ""}
                  onChange={(e) => setForm({ ...form, cta: e.target.value })}
                  className="input-field text-sm mb-2"
                  data-testid="script-cta-select"
                >
                  <option value="">Pick from your saved CTAs or write a new one...</option>
                  {ctaOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={form.cta}
                onChange={(e) => setForm({ ...form, cta: e.target.value })}
                placeholder={ctaOptions.length === 0
                  ? "Set a default CTA in Brand Knowledge → North Star, or type one here"
                  : "Or write a custom CTA for this video..."}
                data-testid="script-cta-input"
                className="input-field text-sm"
              />
              {ctaOptions.length === 0 && (
                <p className="text-[10px] text-amber-600 mt-1.5">
                  💡 Tip: Set your default CTAs in <strong>Brand Knowledge → North Star</strong> so you never have to retype them.
                </p>
              )}
            </div>
          </div>

          <button
            onClick={generateScript}
            disabled={!form.topic || loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="generate-script-btn"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </>
            ) : (
              <>
                <Sparkle size={18} />
                Generate Script
              </>
            )}
          </button>
        </div>

        {/* Output */}
        <div className="space-y-4">
          <EditableScript content={output} setContent={setOutput} loading={loading} />
          
          {/* Save to Pipeline & Voice Generation */}
          {output && !loading && (
            <>
              {/* Save to Pipeline */}
              <div className="card p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Kanban size={24} className="text-[#E91E8C]" />
                  <div>
                    <p className="font-semibold text-zinc-900 text-sm">Save to Pipeline</p>
                    <p className="text-xs text-zinc-500">Add this script to your content pipeline</p>
                  </div>
                </div>
                <button
                  onClick={saveToPipeline}
                  disabled={saving || saved}
                  className={`text-sm px-4 py-2 rounded-lg font-semibold transition-all ${
                    saved 
                      ? "bg-green-500 text-white" 
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  }`}
                  data-testid="save-to-pipeline-btn"
                >
                  {saving ? "Saving..." : saved ? "Saved!" : "Save to Pipeline"}
                </button>
              </div>
              
              {/* Voice Generation */}
              <div className="card p-4 space-y-3" data-testid="voice-generation-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <SpeakerHigh size={24} className="text-[#E91E8C]" />
                    <div>
                      <p className="font-semibold text-zinc-900 text-sm">Generate Voice</p>
                      <p className="text-xs text-zinc-500">
                        {voiceProvider === "openai"
                          ? "Free stock voices via OpenAI TTS"
                          : "Your cloned voice via ElevenLabs"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={generateVoice}
                    disabled={generatingVoice || (voiceProvider === "elevenlabs" && !elevenlabsVoice)}
                    className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                    data-testid="generate-voice-btn"
                  >
                    {generatingVoice ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      "Generate Voice"
                    )}
                  </button>
                </div>

                {/* Provider toggle — Stock vs Cloned */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setVoiceProvider("openai"); persistVoicePref("openai", openaiVoice); }}
                    data-testid="voice-provider-openai"
                    className={`p-2 rounded-lg border text-left transition-all ${
                      voiceProvider === "openai"
                        ? "border-[#E91E8C] bg-[#E91E8C]/5"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <p className="text-xs font-bold text-zinc-900">🆓 Stock Voice</p>
                    <p className="text-[10px] text-zinc-500">9 free preset voices · no setup</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVoiceProvider("elevenlabs"); if (elevenlabsVoice) persistVoicePref("elevenlabs", elevenlabsVoice); }}
                    data-testid="voice-provider-elevenlabs"
                    className={`p-2 rounded-lg border text-left transition-all ${
                      voiceProvider === "elevenlabs"
                        ? "border-[#E91E8C] bg-[#E91E8C]/5"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <p className="text-xs font-bold text-zinc-900">🎤 My Cloned Voice</p>
                    <p className="text-[10px] text-zinc-500">
                      {elevenlabsAvailable
                        ? `ElevenLabs · ${elevenlabsVoices.filter((v) => v.category === "cloned").length} cloned voices`
                        : "ElevenLabs · BYOK required"}
                    </p>
                  </button>
                </div>

                {/* OpenAI voice picker */}
                {voiceProvider === "openai" && (
                  <div className="space-y-1.5" data-testid="openai-voice-picker">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Pick a voice — ❤ = favorites first</p>
                    <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto">
                      {[...openaiVoices]
                        .sort((a, b) => {
                          const aFav = voiceFavorites.includes(`openai:${a.id}`) ? 0 : 1;
                          const bFav = voiceFavorites.includes(`openai:${b.id}`) ? 0 : 1;
                          return aFav - bFav;
                        })
                        .map((v) => {
                          const isFav = voiceFavorites.includes(`openai:${v.id}`);
                          return (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => { setOpenaiVoice(v.id); persistVoicePref("openai", v.id); }}
                              data-testid={`openai-voice-${v.id}`}
                              className={`p-2 rounded-lg border text-left transition-all ${
                                openaiVoice === v.id
                                  ? "border-[#E91E8C] bg-[#E91E8C]/5"
                                  : "border-zinc-200 hover:border-zinc-300"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <p className="text-xs font-bold text-zinc-900 truncate">{v.label}</p>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => toggleVoiceFavorite("openai", v.id, e)}
                                  onKeyDown={(e) => { if (e.key === "Enter") toggleVoiceFavorite("openai", v.id, e); }}
                                  className={`text-sm cursor-pointer leading-none ${isFav ? "" : "opacity-30 hover:opacity-70"}`}
                                  data-testid={`favorite-openai-${v.id}`}
                                  title={isFav ? "Remove from favorites" : "Add to favorites"}
                                >
                                  {isFav ? "❤️" : "🤍"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between mt-0.5">
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); previewVoice(v.id); } }}
                                  className="text-[9px] text-[#E91E8C] hover:text-[#C8186F] font-semibold cursor-pointer"
                                  data-testid={`preview-${v.id}`}
                                >
                                  {previewingVoice === v.id ? "⏵ playing" : "▶ preview"}
                                </span>
                              </div>
                              <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">{v.description}</p>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* ElevenLabs voice picker */}
                {voiceProvider === "elevenlabs" && (
                  <div className="space-y-1.5" data-testid="elevenlabs-voice-picker">
                    {!elevenlabsAvailable ? (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1.5">
                        <p className="font-semibold">ElevenLabs not connected yet</p>
                        <p className="text-amber-800">
                          Sign up free at{" "}
                          <a href="https://elevenlabs.io/app/sign-up" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                            elevenlabs.io
                          </a>{" "}
                          (no card required, 10k chars/mo free) → copy your API key → paste it in{" "}
                          <a href="/account-settings" className="underline font-semibold">Account Settings → API Keys</a>.
                        </p>
                      </div>
                    ) : elevenlabsVoices.length === 0 ? (
                      <p className="text-xs text-zinc-500">No voices found on your ElevenLabs account. Clone one at elevenlabs.io/app/voice-lab.</p>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Your ElevenLabs voices</p>
                        <select
                          value={elevenlabsVoice}
                          onChange={(e) => { setElevenlabsVoice(e.target.value); persistVoicePref("elevenlabs", e.target.value); }}
                          data-testid="elevenlabs-voice-select"
                          className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5"
                        >
                          <option value="">— pick a voice —</option>
                          {elevenlabsVoices.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name} {v.category === "cloned" ? "(your clone)" : v.category === "professional" ? "(pro)" : ""}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                )}

                {audioUrl && (
                  <div className="mt-2 flex items-center gap-4 p-3 bg-zinc-50 rounded-lg">
                    <button
                      onClick={togglePlay}
                      className="p-3 bg-[#E91E8C] text-white rounded-full hover:bg-[#D8157B] transition-all"
                      data-testid="play-audio-btn"
                    >
                      {isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
                    </button>
                    <div className="flex-1">
                      <audio
                        ref={audioRef}
                        src={audioUrl}
                        onEnded={() => setIsPlaying(false)}
                        className="w-full"
                        controls
                      />
                    </div>
                    <a
                      href={audioUrl}
                      download="script-audio.mp3"
                      className="p-2 text-zinc-500 hover:text-[#E91E8C] transition-all"
                      data-testid="download-audio-btn"
                    >
                      <Download size={20} />
                    </a>
                  </div>
                )}
              </div>

              {/* Avatar Video Generation (Magic Hour Talking Photo · Realistic mode · BYOK) */}
              <div className="card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <VideoCamera size={24} className="text-[#E91E8C]" />
                    <div>
                      <p className="font-semibold text-zinc-900 text-sm">Generate Avatar Video</p>
                      <p className="text-xs text-zinc-500">
                        Powered by <span className="font-semibold text-[#E91E8C]">VEED Fabric 1.0</span> (fast · ~1–2 min) ·
                        Magic Hour fallback for base-video lip-sync
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Twin Picker — shows ALL twins in a scrollable grid */}
                {looks.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Select AI Twin</label>
                      <span className="text-[10px] text-zinc-400">{looks.length} available</span>
                    </div>
                    <div
                      className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[420px] overflow-y-auto pr-1"
                      data-testid="ai-twin-picker"
                    >
                      {looks.map((look) => (
                        <button
                          key={look.id}
                          onClick={() => setSelectedLook(look)}
                          className={`relative aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all ${
                            selectedLook?.id === look.id
                              ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30"
                              : "border-zinc-200 hover:border-zinc-400"
                          }`}
                          data-testid={`look-${look.id}`}
                          title={look.name}
                        >
                          <img src={look.image_url} alt={look.name} className="w-full h-full object-cover" />
                          {selectedLook?.id === look.id && (
                            <>
                              <div className="absolute inset-0 bg-[#E91E8C]/15" />
                              <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#E91E8C] flex items-center justify-center shadow-md">
                                <Check size={14} className="text-white" weight="bold" />
                              </div>
                            </>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/55 to-transparent px-2 py-1">
                            <p className="text-[10px] text-white font-medium truncate">{look.name}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {loadingLooks && (
                  <p className="text-xs text-zinc-400">Loading your AI Twins...</p>
                )}
                {!loadingLooks && looks.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                    No AI Twins yet — upload or generate one in <strong>AI Twin Gallery</strong> first.
                  </div>
                )}

                {/* Voice source indicator */}
                {audioFilename ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 text-xs flex items-center gap-2">
                    <SpeakerHigh size={14} className="text-green-600" />
                    <span className="text-green-700 font-medium">Your cloned voice will be used in the video</span>
                  </div>
                ) : (
                  <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-2.5 text-xs flex items-center gap-2">
                    <SpeakerHigh size={14} className="text-zinc-400" />
                    <span className="text-zinc-500">Auto Create generates your cloned voice from the script automatically — no extra step needed.</span>
                  </div>
                )}

                {/* Provider picker — choose which model renders the video */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Video Model</label>
                    {talkingPhotoProvider === "fabric" && (
                      <select
                        value={talkingPhotoResolution}
                        onChange={(e) => setTalkingPhotoResolution(e.target.value)}
                        className="text-[10px] border border-zinc-200 rounded px-1.5 py-0.5"
                        data-testid="script-studio-resolution"
                      >
                        <option value="480p">480p · $0.10/sec</option>
                        <option value="720p">720p · $0.20/sec</option>
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("auto")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "auto" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="ss-provider-auto"
                      title="Try Fabric first, fall back to Magic Hour automatically"
                    >
                      <p className="text-[11px] font-bold text-zinc-900 leading-tight">Auto ✨</p>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Fabric → MH fallback</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("fabric")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "fabric" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="ss-provider-fabric"
                    >
                      <p className="text-[11px] font-bold text-zinc-900 leading-tight">⚡ Fabric</p>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">~1–2 min · $0.10/s</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("magic_hour")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "magic_hour" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="ss-provider-magic-hour"
                    >
                      <p className="text-[11px] font-bold text-zinc-900 leading-tight">🎯 Magic Hour</p>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">~20–30 min · max likeness</p>
                    </button>
                  </div>
                </div>

                <button
                  onClick={generateHeyGenVideo}
                  disabled={generatingVideo || !output}
                  className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                  data-testid="generate-video-btn"
                >
                  {generatingVideo ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" />
                      {videoStatus === "starting" ? "Starting..." : "Processing video..."}
                    </>
                  ) : (
                    <>
                      <VideoCamera size={16} />
                      Generate Video
                    </>
                  )}
                </button>

                {/* Video Status */}
                {videoStatus === "processing" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-2">
                    <div className="flex items-center gap-2 text-amber-700">
                      <CircleNotch size={16} className="animate-spin" />
                      <span>VEED Fabric is rendering your video. Typical render is ~1–2 min for a 60-sec clip (longer scripts auto-chunk and stitch). You can close this tab — we&apos;ll save it to history when done.</span>
                    </div>
                    {videoJobId && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm("Cancel this render? The credit will not be refunded, but your UI unblocks immediately so you can start a fresh job.")) return;
                          try {
                            await axios.post(`${API}/auto-create/jobs/${videoJobId}/cancel`);
                            setVideoStatus("failed");
                            setVideoError("Cancelled by user");
                            setGeneratingVideo(false);
                          } catch (err) {
                            alert("Cancel failed: " + (err.response?.data?.detail || err.message));
                          }
                        }}
                        data-testid="cancel-video-btn"
                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-all"
                      >
                        <X size={12} weight="bold" /> Cancel render
                      </button>
                    )}
                  </div>
                )}

                {videoStatus === "failed" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    <p className="font-medium">Video generation failed</p>
                    {videoError && <p className="mt-1 text-xs">{videoError}</p>}
                  </div>
                )}

                {/* Video Player */}
                {videoUrl && (
                  <div className="space-y-3">
                    <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-[400px] mx-auto">
                      <video
                        src={videoUrl}
                        controls
                        className="w-full h-full object-contain"
                        data-testid="avatar-video-player"
                      />
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="btn-primary text-sm px-4 py-2 flex items-center gap-2 flex-1 justify-center"
                        data-testid="download-video-btn"
                      >
                        <Download size={16} />
                        Download Video
                      </a>
                      <CopyButton text={videoUrl} label="Copy URL" />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Video History */}
      <div className="mt-8">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-[#E91E8C] transition-colors"
          data-testid="toggle-video-history"
        >
          <VideoCamera size={18} />
          Video History ({videoHistory.length})
          <CaretRight size={14} className={`transition-transform ${showHistory ? "rotate-90" : ""}`} />
        </button>

        {showHistory && (
          <div className="mt-4 space-y-3">
            {videoHistory.length === 0 ? (
              <p className="text-sm text-zinc-400 py-6 text-center">No videos generated yet. Generate your first avatar video above.</p>
            ) : (
              videoHistory.map((v) => (
                <div key={v.id} className="card p-4 flex items-start gap-4" data-testid={`video-history-${v.id}`}>
                  {/* Thumbnail or status indicator */}
                  <div className="w-16 h-24 rounded-lg overflow-hidden bg-zinc-100 flex-shrink-0 flex items-center justify-center">
                    {v.status === "completed" && v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
                    ) : v.status === "completed" && v.video_url ? (
                      <video src={v.video_url} className="w-full h-full object-cover" muted />
                    ) : v.status === "processing" ? (
                      <CircleNotch size={24} className="text-amber-500 animate-spin" />
                    ) : (
                      <VideoCamera size={24} className="text-zinc-300" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-zinc-900 text-sm truncate">{v.script_topic || "Untitled"}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {v.look_name || "bb"} &middot; {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{v.script_preview}</p>

                    {/* Status badge */}
                    <div className="mt-2 flex items-center gap-2">
                      {v.status === "completed" ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Completed</span>
                      ) : v.status === "processing" ? (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Processing...</span>
                      ) : v.status === "failed" ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Failed</span>
                      ) : (
                        <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full font-medium">{v.status}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {v.status === "completed" && v.video_url && (
                      <>
                        <a
                          href={v.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="p-2 text-zinc-500 hover:text-[#E91E8C] transition-all rounded-lg hover:bg-zinc-50"
                          title="Download"
                          data-testid={`download-history-${v.id}`}
                        >
                          <Download size={18} />
                        </a>
                        <CopyButton text={v.video_url} label="" />
                      </>
                    )}
                    <button
                      onClick={() => deleteVideoFromHistory(v.id)}
                      className="p-2 text-zinc-400 hover:text-red-500 transition-all rounded-lg hover:bg-zinc-50"
                      title="Remove"
                      data-testid={`delete-history-${v.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptStudio;

/* ─── EditableScript ─────────────────────────────────────────────────
 * Inline-editable version of AIOutput. Lets the user tweak the script
 * before pressing "Generate Avatar Video" or "Save to Pipeline". Edits
 * flow back to the parent via setContent(text). Read-only "view" mode
 * by default for cleaner reading; "edit" mode swaps to a textarea.
 * ──────────────────────────────────────────────────────────────────── */
const EditableScript = ({ content, setContent, loading }) => {
  const [editing, setEditing] = useState(false);
  const [copyOk, setCopyOk] = useState(false);

  if (loading) {
    return (
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkle size={20} className="text-[#E91E8C] animate-pulse" />
          <span className="text-zinc-500">Generating content...</span>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-zinc-100 rounded animate-pulse"></div>
          <div className="h-4 bg-zinc-100 rounded animate-pulse w-3/4"></div>
          <div className="h-4 bg-zinc-100 rounded animate-pulse w-1/2"></div>
        </div>
      </div>
    );
  }
  if (!content) return null;

  const wordCount = (content || "").trim().split(/\s+/).filter(Boolean).length;
  const estSeconds = Math.round((wordCount / 150) * 60); // 150 wpm avg

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <div className="card p-4 sm:p-6" data-testid="editable-script-card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <h4 className="font-semibold text-zinc-900">Your Script</h4>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            ~{wordCount} words · est. {estSeconds < 60 ? `${estSeconds}s` : `${(estSeconds / 60).toFixed(1)} min`} spoken
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setEditing(v => !v)}
            className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-all ${
              editing ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            }`}
            data-testid="script-edit-toggle"
          >
            <PencilSimple size={14} weight="bold" />
            {editing ? "Done" : "Edit"}
          </button>
          <button
            onClick={onCopy}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-[#E91E8C] transition-all px-2.5 py-1.5 rounded-lg hover:bg-zinc-50"
            data-testid="script-copy-btn"
          >
            <Copy size={14} />
            {copyOk ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full bg-white border-2 border-[#E91E8C] rounded-lg p-4 text-zinc-800 text-sm leading-relaxed font-mono min-h-[300px] focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 resize-y"
          spellCheck
          data-testid="script-edit-textarea"
        />
      ) : (
        <div
          className="bg-zinc-50 hover:bg-zinc-100/60 cursor-text rounded-lg p-4 whitespace-pre-wrap text-zinc-700 text-sm leading-relaxed max-h-[500px] overflow-y-auto transition-colors"
          onClick={() => setEditing(true)}
          data-testid="script-view-area"
          title="Click to edit"
        >
          {content}
        </div>
      )}
      {editing && (
        <p className="text-[11px] text-zinc-400 mt-2">
          Your edits are saved as you type. They&apos;ll be used as-is when you generate the avatar video below.
        </p>
      )}
    </div>
  );
};
